import "./style.scss";

import { ButtonIconPosition, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import IconExternalLink from "@amzn/stencil-react-icons/icons/icon-external-link";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import React, { FC } from "react";

/**
 * Represents the props for the HardwareSupportOptions component.
 * @interface
 * @property {() => void} [onButtonClick] - Callback function triggered when the shipment or techinal buttons are clicked.
 * @property {() => void} [onOrderClick] - Callback function triggered when the order button is clicked.
 */
interface HardwareSupportOptionsProps {
  onButtonClick: () => void;
  onOrderClick: () => void;
}

const OPENS_IN_NEW_TAB = getString("aria.opensInNewTab");

export const HardwareSupportOptions: FC<HardwareSupportOptionsProps> = ({ onButtonClick, onOrderClick }) => {
  // Feature flag to show phase 2 of Hardware Order Support
  const useHardwareOrderSupport = UserPermissions.current.hasPermission(PERMISSIONS.USE_SUPPORT_PHASE_2);

  return (
    <Col gridGap="S600">
      {/* Row 1 */}
      <Row className="flyout-row checklist">
        <Col className="flyout-text">
          <Text fontSize="T300" fontWeight="bold">
            {getString("hardwareSupport.orderTitle")}
          </Text>
          <Spacer height="S200" />
          <Text fontSize="T200">{getString("hardwareSupport.orderDescription")}</Text>
          <Spacer height="S300" />
          {useHardwareOrderSupport ? (
            <Button
              className="flyout-button"
              touchpoint="get-order-support-button"
              variant={ButtonVariant.Secondary}
              iconPosition={ButtonIconPosition.Trailing}
              onClick={onOrderClick}
            >
              {getString("hardwareSupport.orderButton")}
            </Button>
          ) : (
            <Link
              href={getString("externalLinks.hardwareOrderSupportTicketUrl")}
              target="_blank"
              aria-label={`${getString("hardwareSupport.orderButton")} - ${OPENS_IN_NEW_TAB}`}
            >
              <Button
                className="flyout-button"
                variant={ButtonVariant.Secondary}
                icon={<IconExternalLink aria-hidden={true} />}
                iconPosition={ButtonIconPosition.Trailing}
                touchpoint="order-support-ticket-link"
                onClick={onButtonClick}
              >
                {getString("hardwareSupport.orderButton")}
              </Button>
            </Link>
          )}
        </Col>
        <div className="flyout-image"></div>
      </Row>

      {/* Row 2 */}
      <Row className="flyout-row box">
        <Col className="flyout-text">
          <Text fontSize="T300" fontWeight="bold">
            {getString("hardwareSupport.shipmentTitle")}
          </Text>
          <Spacer height="S200" />
          <Text fontSize="T200">{getString("hardwareSupport.shipmentDescription")}</Text>
          <Spacer height="S300" />
          <Link
            href={getString("externalLinks.hardwareTicketUrl")}
            target="_blank"
            aria-label={`${getString("hardwareSupport.shipmentButton")} - ${OPENS_IN_NEW_TAB}`}
          >
            <Button
              className="flyout-button"
              variant={ButtonVariant.Secondary}
              icon={<IconExternalLink aria-hidden={true} />}
              iconPosition={ButtonIconPosition.Trailing}
              touchpoint="get-shipment-support-link"
              onClick={onButtonClick}
            >
              {getString("hardwareSupport.shipmentButton")}
            </Button>
          </Link>
        </Col>
        <div className="flyout-image"></div>
      </Row>

      {/* Row 3 */}
      <Row className="flyout-row gears">
        <Col className="flyout-text">
          <Text fontSize="T300" fontWeight="bold">
            {getString("hardwareSupport.technicalTitle")}
          </Text>
          <Spacer height="S200" />
          <Text fontSize="T200">{getString("hardwareSupport.technicalDescription")}</Text>
          <Spacer height="S300" />
          <Link
            href={getString("externalLinks.technicalSupportUrl")}
            target="_blank"
            aria-label={`${getString("hardwareSupport.technicalButton")} - ${OPENS_IN_NEW_TAB}`}
          >
            <Button
              className="flyout-button"
              variant={ButtonVariant.Secondary}
              icon={<IconExternalLink aria-hidden={true} />}
              iconPosition={ButtonIconPosition.Trailing}
              touchpoint="get-technical-support-link"
              onClick={onButtonClick}
            >
              {getString("hardwareSupport.technicalButton")}
            </Button>
          </Link>
        </Col>
        <div className="flyout-image"></div>
      </Row>
    </Col>
  );
};
